import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'
import * as Popover from '@radix-ui/react-popover'

import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import SvgX from '@jotta/ui/SvgX'
import { useHideFabOnMobile } from '@jotta/ui/useHideFab'
import { useHideModuleNavOnMobile } from '@jotta/ui/useHideModuleNav'

import { useRenamePathSubmitHandler } from './hooks/useRenamePath'
import type { PathItemStore } from './PathItemStore'
import { useSelectNamePart } from './useSelectNamePart'

import styles from './PathItemRow.module.scss'

export interface PathItemThumbRenameProps {
  item: PathItemStore
}

export const PathItemThumbRename = observer<PathItemThumbRenameProps>(
  function PathItemThumbRename({ item }) {
    const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null)
    const open = item.showRenameInput

    useHideFabOnMobile(Boolean(inputEl))
    useHideModuleNavOnMobile(Boolean(inputEl))
    useSelectNamePart(inputEl)

    useEffect(() => {
      if (open) {
        document.body.classList.add('fab-open')
      }

      return () => {
        document.body.classList.remove('fab-open')
      }
    }, [open])

    const newName = item.model.name
    const { onCancel, onSubmit, errorMessage } = useRenamePathSubmitHandler(
      item,
      newName,
    )

    return (
      <Popover.Root
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            onCancel()
          }
        }}
        modal={true}
      >
        <Popover.Trigger asChild>
          <div className={styles.renamePopoverTrigger} />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content data-rename className={styles.renamePopover}>
            <div className={styles.renamePopoverTitle}>
              <p>
                <Trans>Change name</Trans>
              </p>
              <Popover.Close>
                <PlainBrandIcon icon={SvgX} />
              </Popover.Close>
            </div>
            <form onSubmit={onSubmit}>
              <input
                ref={setInputEl}
                data-isfile={item.isFile}
                type="text"
                className="input"
                value={newName}
                aria-invalid={Boolean(errorMessage)}
                onChange={item.onNameInputChange}
              />
              <Button className="btn btn-primary" type="submit">
                <Trans>Ok</Trans>
              </Button>
            </form>
            <Popover.Arrow />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    )
  },
)
